import { Spinner, toast } from '@raystack/apsara';
import type { AxiosError, AxiosResponse } from 'axios';

import api from '@src/api';
import { DexterV1AlphaInferenceDownloadPayload } from '@src/containers/Viewer/subComponents/Insights/types/insights';
import { download, visitor } from '@src/utils/common';

type ResultSuccess = {
  download_id: 'string';
  status: 'string';
  signed_url: 'string';
};

type ResuleFailure = { error: { message: string } };

export type Result = ResultSuccess | ResuleFailure;

function handleBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = 'Download in progress. Are you sure?';
  return 'Download in progress. Are you sure?';
}

function isSuccess(status: string) {
  return status.toLowerCase() === 'success';
}

function isFailed(status: string) {
  return status.toLowerCase() === 'failed';
}

export async function downloadAsset({
  fileName,
  onSuccess,
  onError,
  onFinally,
  startDownloadFn,
  fetchStatusFn
}: {
  fileName: string;
  onSuccess?: VoidFunction;
  onError?: (err: unknown) => void;
  onFinally?: VoidFunction;
  startDownloadFn: () => Promise<AxiosResponse<Result>>;
  fetchStatusFn: (download_id: string) => Promise<AxiosResponse<Result>>;
}) {
  try {
    window.addEventListener('beforeunload', handleBeforeUnload);

    const onStop = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };

    toast.message(
      'Your files are being prepared. The download will start automatically in few minutes.',
      { icon: <Spinner size={2} />, id: 'downloading-asset-toast' }
    );

    const res = await startDownloadFn();

    if ('status' in res.data) {
      if (isSuccess(res.data.status)) {
        onStop();
        download(res, fileName);
      } else {
        const { getResult } = visitor<typeof res>({
          fn: () => fetchStatusFn((res.data as ResultSuccess).download_id),
          stopCondition: res =>
            isSuccess((res.data as ResultSuccess).status) ||
            isFailed((res.data as ResultSuccess).status),
          onStop
        });

        const response = await getResult();

        if ('error' in response.data || isFailed(response.data.status)) {
          throw response.data;
        }

        download(await getResult(), fileName);
      }
    } else {
      throw res.data;
    }

    toast.dismiss('downloading-asset-toast');
    onSuccess?.();
  } catch (err: unknown) {
    console.error(err);
    const message = (err as AxiosError)?.response?.data.error?.message;
    console.log(message);
    if (message) toast.error(message);
    onError?.(err);
  } finally {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    onFinally?.();
  }
}

export function downloadAssetApis() {
  function getAtlasAssetByAoiId(
    aoi_id: string,
    download_id: string
  ): Promise<AxiosResponse<Result>> {
    return api.AtlasPoll.get(`/aois/${aoi_id}/downloads/${download_id}`);
  }

  function postAtlasAssetByAoiId(
    aoi_id: string,
    asset_ids: string[]
  ): Promise<AxiosResponse<Result>> {
    return api.AtlasPoll.post<Result>(`/aois/${aoi_id}/downloads`, {
      asset_ids
    });
  }

  function postDexterAssetByInsightId(insight_id: string) {
    return api.DexterPoll.put(`/v1alpha1/inferences/${insight_id}/downloads`);
  }

  function putDexterMultipleAssetsByInsightId(
    insight_id: string,
    payload: DexterV1AlphaInferenceDownloadPayload
  ) {
    return api.DexterPoll.put(
      `/v1alpha1/inferences/${insight_id}/downloads`,
      payload
    );
  }

  function postDexterAssetByWorkflowTaskId(
    workflow_id: string,
    job_id: string,
    task_id: string
  ) {
    return api.DexterPoll.put(
      `/v1alpha1/workflows/${workflow_id}/jobs/${job_id}/tasks/${task_id}/downloads`
    );
  }

  function postDexterAssetByWorflowJobId(workflow_id: string, job_id: string) {
    return api.DexterPoll.put(
      `/v1alpha1/workflows/${workflow_id}/jobs/${job_id}/downloads`
    );
  }

  return {
    getAtlasAssetByAoiId,
    postAtlasAssetByAoiId,
    postDexterAssetByInsightId,
    putDexterMultipleAssetsByInsightId,
    postDexterAssetByWorkflowTaskId,
    postDexterAssetByWorflowJobId
  };
}
